<template>
  <base-sticky v-slot="{ isStuck }" :margin="header.sticky.offset" name="headerPLP">
    <div
      class="sticky z-header"
      :class="{ 'shadow-bottom': isStuck }"
      :style="{ top: pxToRem(stickyHeaderTop) }"
    >
      <div :class="{ 'md:container md:px-4': !isStuck }">
        <div class="w-full bg-grey-90">
          <div
            class="mx-a flex items-center px-4 py-4 text-xs font-secondary uppercase between container"
          >
            <div aria-live="polite">
              {{ total }} {{ $pluralize($t.products, total) }}
            </div>
            <base-button
              :aria-expanded="filtersShown"
              class="flex items-center gap-1 uppercase"
              data-test-id="product-list-header-filters-button"
              @click="() => PanelFilters.open()"
            >
              {{ $t.showFilters }}
              <vf-icon name="filters" size="md" />
            </base-button>
          </div>
        </div>
        <section
          v-if="appliedFilters.length || storeFilterState"
          :aria-label="$t.appliedFilters"
          class="flex gap-4 bg-white px-4 py-2 between md:container"
        >
          <base-button class="flex shrink-0 self-center text-sm underlined md:hidden" @click="clearAll">
            {{ $t.clearAll }}
          </base-button>
          <div
            ref="appliedFiltersRef"
            :id="`${id}-content`"
            :aria-labelledby="id"
            class="relative flex gap-2 overflow-y-hidden transition-height,max-height scrollbar-none md:wrap"
            role="region"
            :style="{ maxHeight: expanded ? scrollHeight : maxHeight }"
          >
            <vf-pill v-if="storeFilterState" class="uppercase" removable @click="emit('changeStore', undefined)">
              {{ $t.pickupInStore }}
            </vf-pill>
            <vf-pill
              v-for="(item, i) in appliedFilters"
              :key="i + item.code"
              class="uppercase"
              removable
              @click="emit('remove', { code: item.code, id: 'options' in item && item.options?.[0]?.id })"
            >
              <span class="sr-only">{{ $t.removeAppliedFilter }}</span>
              <template v-if="'options' in item && item.options?.[0] && item.type === 'ColorSwatch'" #before>
                <vf-color-swatch
                  :key="item.options[0].color"
                  :id="item.options[0].id"
                  class="block h-4 w-4"
                  class-inner="rounded-full overflow-hidden"
                  :color="item.options[0].color"
                />
              </template>
              <template v-if="item.selected && item.type === 'TilePercentage' && !item.selected.includes('%')" #after>
                %
              </template>
              <template v-if="item.selected && item.type === 'PriceInput'">
                {{ $formatPrice({ low: +item.selected[0], high: +item.selected[1] },
                                currency,
                                { trailingZeroDisplay: 'stripIfInteger' }) }}
              </template>
              <template v-else-if="item.selected && item.type === 'Search'">
                {{ item.selected[0] }}
              </template>
              <template v-else-if="'options' in item && item.options">
                <template v-for="({ label, id: optionId }) in item.options" :key="optionId">
                  {{ label }}
                </template>
              </template>
            </vf-pill>
          </div>
          <div class="hidden h-6 shrink-0 gap-2 between md:flex">
            <button
              v-if="isOverflowing"
              :id
              :aria-controls="`${id}-content`"
              :aria-expanded="expanded"
              class="flex items-center gap-1 text-sm underlined"
              @click="expanded = !expanded"
            >
              {{ expanded ? $t.seeLess : $t.seeMore }}
              <vf-icon :dir="expanded ? 'up' : 'down'" name="chevron" />
            </button>
            <base-button class="shrink-0 text-sm underlined" @click="clearAll">
              {{ $t.clearAll }}
            </base-button>
          </div>
        </section>
      </div>
    </div>
    <panel-filters v-slot="attrs">
      <slot v-bind="attrs" />
    </panel-filters>
  </base-sticky>
</template>

<script lang="ts" setup>
import type { Emits, Props } from '#commerce/components/product/list/header/Header.vue'
import type { FilterItem } from '#types/filters'

const { currency, filters, list, storeFilterState } = defineProps<Props>()
const emit = defineEmits<Emits>()
const header = useHeaderStore()
const id = useId()
const filtersShown = defineModel('filtersShown')
const PanelFilters = createPanel('filters')
const total = computed(() => list?.total || 0)
const appliedFiltersRef = ref<HTMLDivElement>()
const expanded = ref(false)
const { y } = useScroll(document)

const stickyHeaderTop = computed(() =>
  header.pageScrolledUp || y.value <= 25
    ? header.height.header
    : header.sticky.offset
)

const appliedFilters = computed(() => Object.values(filters)
  .map((item: FilterItem) => ({
    type: item.type,
    code: item.code,
    selected: item.selected,
    options: 'options' in item ? item.options?.filter(({ id }) => item.selected?.includes(id)) : []
  }))
  .reduce<(Omit<FilterItem, 'label' | 'layout' | 'facetId'>)[]>((accumulator, { type, code, options, selected }) =>
    (accumulator.concat(
      ((type === 'PriceInput' || type === 'Search') && selected?.filter(Boolean).length)
        ? [{ type, code, selected }]
        : (options || []).map((option) =>
            ({ type, code, selected, options: [option] })
          )
    )
    ), []))

const { isOverflowing, maxHeight, scrollHeight } = useOverflowCheck(appliedFiltersRef, {
  maxRows: 1,
  watch: () => appliedFilters.value.length
})

const clearAll = () => {
  emit('clear')
}
</script>
